// Light/dark theme, applied via a `data-theme` attribute on <html>. The
// preference ("light" | "dark" | "system") is persisted; "system" (the default
// when unset) follows the OS `prefers-color-scheme`. An inline script in
// index.html sets the initial attribute before first paint to avoid a flash —
// initTheme() then re-applies the same value (idempotent) and wires the rest.

import { reachGoal, setUserParams } from "./analytics/yandex-metrika";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeStore {
  getTheme(): string | null;
  setTheme(theme: string): void;
}

const DARK_QUERY = "(prefers-color-scheme: dark)";

// Fallback address-bar colours if the CSS custom property can't be read yet.
const CHROME_FALLBACK: Record<ResolvedTheme, string> = {
  light: "#faf8ef",
  dark: "#261e16",
};

let toggleButton: HTMLButtonElement | null = null;

function isPreference(value: string | null): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

export function getPreference(store: ThemeStore): ThemePreference {
  const raw = store.getTheme();
  return isPreference(raw) ? raw : "system";
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

function resolve(pref: ThemePreference): ResolvedTheme {
  return pref === "system" ? systemTheme() : pref;
}

/** The theme currently applied to the document. */
export function getTheme(): ResolvedTheme {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

// Match the address bar to the active theme's page background, read from CSS so
// it tracks whichever dark palette ships (no manual coupling to the SCSS value).
function pageBackground(theme: ResolvedTheme): string {
  const fromCss = getComputedStyle(document.documentElement)
    .getPropertyValue("--color-page-bg")
    .trim();
  return fromCss || CHROME_FALLBACK[theme];
}

function updateChrome(theme: ResolvedTheme): void {
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", pageBackground(theme));

  document
    .querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
    ?.setAttribute("content", theme === "dark" ? "black-translucent" : "black");
}

function refreshToggle(theme: ResolvedTheme): void {
  if (!toggleButton) return;
  const target: ResolvedTheme = theme === "dark" ? "light" : "dark";
  toggleButton.setAttribute("aria-label", `Switch to ${target} theme`);
  toggleButton.setAttribute("aria-pressed", String(theme === "dark"));
}

function apply(theme: ResolvedTheme): void {
  document.documentElement.dataset.theme = theme;
  updateChrome(theme);
  refreshToggle(theme);
}

export function setTheme(pref: ThemePreference, store: ThemeStore): void {
  store.setTheme(pref);
  apply(resolve(pref));
}

/** Flip between light and dark, persisting the explicit choice. */
export function toggleTheme(store: ThemeStore): ResolvedTheme {
  const wasAuto = getPreference(store) === "system";
  const next: ResolvedTheme = getTheme() === "dark" ? "light" : "dark";
  setTheme(next, store);
  reportTheme(next, "manual");
  // Discrete events worth counting as goals.
  if (next === "dark") {
    reachGoal("theme-set-dark");
  } else if (wasAuto) {
    // Switching to light while the pref was still "system" = turning OFF an
    // auto-applied dark theme.
    reachGoal("theme-revert-auto-dark");
  }
  return next;
}

// Record the theme + how the player got it as persistent, segmentable Metrika
// visitor parameters (adoption + usefulness), separate from the goal events.
function reportTheme(theme: ResolvedTheme, source: "auto" | "manual"): void {
  setUserParams({ theme, themeSource: source });
}

/** Apply the stored/system theme and keep following the OS while unset. */
export function initTheme(store: ThemeStore): void {
  const pref = getPreference(store);
  const resolved = resolve(pref);
  apply(resolved);
  reportTheme(resolved, pref === "system" ? "auto" : "manual");
  window.matchMedia(DARK_QUERY).addEventListener("change", () => {
    if (getPreference(store) === "system") {
      const next = systemTheme();
      apply(next);
      reportTheme(next, "auto");
    }
  });
}

/**
 * Create the sun/moon theme toggle. CSS places it in the page's top-right corner
 * (`position: absolute`, so it scrolls with the page) at all widths; it's inserted
 * into `.above-game` only as a DOM anchor/fallback, so source order has no effect.
 */
export function createThemeToggle(store: ThemeStore): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "theme-toggle";

  // Decorative day/night switch — sun/moon SVGs in the sliding thumb, plus
  // clouds (day) / stars (night). The state is driven purely by the
  // `data-theme` attribute in CSS (see main.scss).
  const span = (cls: string): HTMLSpanElement => {
    const el = document.createElement("span");
    el.className = cls;
    return el;
  };
  const track = span("theme-toggle-track");
  track.setAttribute("aria-hidden", "true");
  const thumb = span("theme-toggle-thumb");
  thumb.append(span("theme-toggle-sun"), span("theme-toggle-moon"));
  track.append(span("theme-toggle-stars"), span("theme-toggle-clouds"), thumb);
  button.appendChild(track);

  button.addEventListener("click", () => {
    toggleTheme(store);
  });

  toggleButton = button;
  refreshToggle(getTheme());

  const aboveGame = document.querySelector(".above-game");
  const restart = aboveGame?.querySelector(".restart-button") ?? null;
  if (aboveGame && restart) {
    aboveGame.insertBefore(button, restart);
  } else {
    (aboveGame ?? document.body).appendChild(button);
  }
  return button;
}
